<script setup lang="ts">
import { computed, ref } from "vue";
import Button from "primevue/button";
import InputText from "primevue/inputtext";
import Message from "primevue/message";
import Password from "primevue/password";
import FloatLabel from "primevue/floatlabel";
import Chip from "primevue/chip";
import { useAuthStore, useAuthModal } from "@/features/auth/store";
import { useAuthErrors } from "@/composables/useAuthErrors";
import { useI18n } from "vue-i18n";
import Divider from "primevue/divider";

const emits = defineEmits(["closeModal"]);

const { handleAuthError, showSuccessToast } = useAuthErrors();
const { t } = useI18n();

const name = ref("");
const usernameError = ref("");

const valuePassword = ref("");
const passwordError = ref("");
const loadingState = ref(false);

const sixSymbolsValidation = computed(() => valuePassword.value.length > 6);
const numberValidation = computed(() => /\d/.test(valuePassword.value));
const bigLetterValidation = computed(() => /\p{Lu}/u.test(valuePassword.value));
const smallLetterValidation = computed(() => /\p{Ll}/u.test(valuePassword.value));
const specialLetterValidation = computed(() =>
  /[!@#$%^&*(),.?":{}|<>]/.test(valuePassword.value)
);

const passwordValidations = computed(() => [
  { label: t("password_validation.min_6_chars"), validation: sixSymbolsValidation.value },
  { label: t("password_validation.one_number"), validation: numberValidation.value },
  {
    label: t("password_validation.one_uppercase"),
    validation: bigLetterValidation.value,
  },
  {
    label: t("password_validation.one_lowercase"),
    validation: smallLetterValidation.value,
  },
  {
    label: t("password_validation.one_special_char"),
    validation: specialLetterValidation.value,
  },
]);

const disableSubmit = computed(() => {
  return (
    name.value === "" ||
    valuePassword.value === "" ||
    !!usernameError.value.length ||
    !!passwordError.value.length
  );
});

function createAccountHandler() {
  useAuthModal().showAuthModal("signUp");
}

const validateUsername = () => {
  usernameError.value = name.value.length >= 3 ? "" : t("signup.username_error");
};

const validatePassword = () => {
  passwordError.value = valuePassword.value.length >= 8 ? "" : t("signup.password_error");
};

function forgotPasswordHandler() {
  useAuthModal().showAuthModal("resetPassword");
}

const handleLogin = async () => {
  loadingState.value = true;
  try {
    const res = await useAuthStore().auth({
      email: name.value,
      password: valuePassword.value,
    });

    if (res.status === 200) {
      showSuccessToast("Добро пожаловать!", "Успешный вход");
      useAuthModal().hideAuthModal();
    }
  } catch (error) {
    handleAuthError(error, "Login");
  } finally {
    loadingState.value = false;
  }
};

const handleThirdPartyLogin = async (provider: string) => {
  await useAuthStore().thirdPartyAuth(provider);
};
</script>

<template>
  <div class="sign-in__content">
    <div class="sign-in__content__wrapper">
      <div class="sign-in__inputs">
        <div>
          <FloatLabel>
            <InputText
              id="name"
              v-model="name"
              @input="validateUsername"
              name="name"
              autocomplete="name"
              class="name"
              :pt="{
                root: {
                  style: {
                    width: '100%',
                    borderRadius: '8px',
                  },
                },
              }"
            />
            <label for="name">{{ t("signup.username_label") }}</label>
          </FloatLabel>
          <Message
            v-if="!!usernameError"
            severity="error"
            size="small"
            :pt="{
              root: {
                style: {
                  outline: 'none',
                },
              },
              content: {
                style: {
                  padding: '0',
                  background: 'transparent',
                },
              },
              text: {
                style: {
                  paddingLeft: '16px',
                  fontSize: '12px',
                },
              },
            }"
            >{{ usernameError }}
          </Message>
        </div>
        <div>
          <FloatLabel>
            <Password
              v-model="valuePassword"
              @input="validatePassword"
              @blur="validatePassword"
              :invalid="!!passwordError"
              :feedback="valuePassword.length >= 1"
              class="password"
              type="password"
              aria-label="password"
              autocomplete="current-password"
              name="password"
              toggle-mask
              :pt="{
                root: {
                  style: {
                    width: '100%',
                    borderRadius: '8px',
                  },
                },

                pcInput: {
                  root: {
                    style: {
                      width: '100%',
                      borderRadius: '8px',
                    },
                  },
                },
              }"
            >
              <template #footer>
                <div class="password__validation-chips-wrapper">
                  <Chip
                    v-for="(item, index) in passwordValidations"
                    :key="index"
                    :label="item.label"
                    :icon="`pi ${
                      item.validation ? 'pi-chevron-circle-down' : 'pi-circle'
                    }`"
                    :pt="{
                      root: {
                        style: {
                          backgroundColor: item.validation
                            ? 'rgba(14, 105, 17, 0.05)'
                            : 'rgba(26, 33, 51, 0.05)',
                          color: item.validation
                            ? 'var(--green-100)'
                            : 'var(--primary-blue-80)',
                        },
                      },
                      icon: {
                        style: {
                          color: item.validation
                            ? 'var(--green-100)'
                            : 'var(--primary-blue-80)',
                        },
                      },
                    }"
                  />
                </div>
              </template>
            </Password>
            <label for="password">{{ t("signup.password_label") }}</label>
          </FloatLabel>
          <Message
            v-if="!!passwordError"
            severity="error"
            size="small"
            :pt="{
              root: {
                style: {
                  outline: 'none',
                },
              },
              content: {
                style: {
                  padding: '0',
                  background: 'transparent',
                },
              },
              text: {
                style: {
                  paddingLeft: '16px',
                  fontSize: '12px',
                },
              },
            }"
            >{{ passwordError }}
          </Message>
        </div>
      </div>
      <div class="forgot__password">
        <Button
          class="forgot__password"
          :label="t('reset_password.forgot_password')"
          text
          :pt="{
            root: {
              style: {
                padding: '0',
              },
            },
          }"
          @click="forgotPasswordHandler"
        />
      </div>
    </div>
    <div class="sign-in__submit_btn__wrapper">
      <Button
        class="sign-in-submit"
        :label="t('signup.sign_in_button')"
        :disabled="disableSubmit"
        :loading="loadingState"
        :pt="{
          root: {
            style: {
              width: '100%',
              height: '48px',
            },
          },
        }"
        @click="handleLogin"
      />
    </div>
  </div>
</template>
<style scoped>
.sign-in__content {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.sign-in__content__wrapper {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  width: 100%;
  padding: 20px 0;
  gap: 8px;
}

.sign-in__inputs {
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 20px;
}

.sign-in__submit_btn__wrapper {
  width: 100%;
}

.name,
.password {
  width: 100%;
}

.password__validation-chips-wrapper {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  max-width: 280px;
}
</style>
